import { useEffect, useState } from "react";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient
} from "@tanstack/react-query";
import { toast } from "react-toastify";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { cx } from "shared/utils/classnames";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { Template } from "types/landlord/verificationTemplate";
import { getErrorMessage } from "utils/error";
import templatesApi, { GetTemplatesResponse } from "api/landlord/templates";
import { ChangeTemplateWarningModal } from "./ChangeTemplateWarningModal";
import { ApplicationStatus } from "shared/types";

const PER_PAGE = 20;

export const ChangeTemplateSubmenu = ({
  applicationId,
  applicationStatus,
  currentTemplate,
  onTemplateChanged,
  contentClassName
}: {
  applicationId: string;
  applicationStatus?: ApplicationStatus;
  currentTemplate?: Template;
  onTemplateChanged?: () => void;
  contentClassName?: string;
}) => {
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null
  );

  const queryClient = useQueryClient();

  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useInfiniteQuery<GetTemplatesResponse>({
      queryKey: ["TEMPLATES"],
      queryFn: ({ pageParam }) =>
        templatesApi.getTemplates(Number(pageParam), PER_PAGE),
      initialPageParam: 1,
      getNextPageParam: (lastPage) =>
        lastPage.has_more ? lastPage.current_page + 1 : undefined
    });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, data?.pages.length]);

  const { mutate: changeTemplate, isPending } = useMutation({
    mutationFn: () => {
      if (!selectedTemplateId) throw new Error("No template selected");
      return templatesApi.changeApplicationTemplate(
        applicationId,
        selectedTemplateId
      );
    },
    mutationKey: ["CHANGE_APPLICATION_TEMPLATE", applicationId],
    onSuccess: (data) => {
      if (data.warnings && data.warnings.length > 0)
        data.warnings.forEach((warning) => {
          toast.warning(warning.message);
        });
      else toast.success("Template changed successfully");

      queryClient.invalidateQueries({
        queryKey: ["APPLICATION", applicationId]
      });
      queryClient.invalidateQueries({
        queryKey: ["APPLICANTS_TABLE"]
      });

      onTemplateChanged?.();
      setShowWarningModal(false);
      setSelectedTemplateId(null);

      // Delay reload so the toast is readable
      setTimeout(() => window.location.reload(), 2000);
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    }
  });

  const isNewerVersionOfCurrent = (template: Template) =>
    currentTemplate !== undefined &&
    template.id === currentTemplate.id &&
    (template.version ?? 0) > (currentTemplate.version ?? 0);

  const allTemplates = data?.pages.flatMap((page) => page.items) ?? [];

  const availableTemplates = allTemplates
    .filter(
      (template) =>
        !(
          currentTemplate !== undefined &&
          template.id === currentTemplate.id &&
          (template.version ?? 0) <= (currentTemplate.version ?? 0)
        )
    )
    .sort((a, b) => {
      const aNew = isNewerVersionOfCurrent(a);
      const bNew = isNewerVersionOfCurrent(b);
      if (aNew !== bNew) return aNew ? -1 : 1;
      return a.name.localeCompare(b.name);
    });

  const isStatusInvalid = !Boolean(
    applicationId &&
    applicationStatus &&
    [ApplicationStatus.Submitted].includes(applicationStatus)
  );

  const hasNoAvailableTemplates =
    !isLoading && !isFetchingNextPage && availableTemplates.length === 0;

  let tooltipMessage: string | null = null;
  if (hasNoAvailableTemplates)
    tooltipMessage = "There are no other templates available to change to";
  else if (isStatusInvalid)
    tooltipMessage =
      "Template change is not available for partially submitted, not submitted, decisioned, canceled, and on-hold applications";

  const disabled = Boolean(tooltipMessage);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplateId(templateId);
    setShowWarningModal(true);
  };

  const handleConfirmChange = () => {
    changeTemplate();
  };

  if (isLoading)
    return (
      <Button
        variant={ButtonVariant.BASE}
        size={ButtonSize.SM}
        className="w-full"
        disabled
        loading
      >
        Change template
      </Button>
    );

  return (
    <>
      <Tooltip open={disabled ? undefined : false} placement="bottom-start">
        <TooltipTrigger asChild>
          <div>
            <ContextMenuPopover
              placement="right-start"
              disabled={disabled}
              contentClassName={cx(
                "!z-[105] w-[309px] max-h-[300px] overflow-y-auto shadow-xl rounded-lg border",
                contentClassName
              )}
              contentBorderless={true}
              cbTrigger={() => (
                <Button
                  variant={ButtonVariant.BASE}
                  size={ButtonSize.SM}
                  className="w-full"
                  disabled={disabled}
                >
                  Change template
                </Button>
              )}
              actions={availableTemplates.map((template) => ({
                title: (
                  <div className="space-y-2">
                    <div className="text-sm font-medium text-content-1000 truncate">
                      {template.name}
                    </div>
                    {isNewerVersionOfCurrent(template) ? (
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-primary-500" />
                        <span className="text-xs font-normal text-content-1000">
                          New version of current template available
                        </span>
                      </div>
                    ) : null}
                  </div>
                ),
                onClick: () => handleTemplateSelect(template.id)
              }))}
            />
          </div>
        </TooltipTrigger>
        {tooltipMessage ? (
          <TooltipContent className="max-w-[400px] rounded-lg bg-alpha-800 p-4 text-sm text-white z-[106] border">
            {tooltipMessage}
          </TooltipContent>
        ) : null}
      </Tooltip>

      <ChangeTemplateWarningModal
        opened={showWarningModal}
        onClose={() => {
          setShowWarningModal(false);
          setSelectedTemplateId(null);
        }}
        onContinue={handleConfirmChange}
        isPending={isPending}
      />
    </>
  );
};
